import { colorGradient } from '@shared/shared.utils'
import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core'
import { SetDefaultData } from '@containers/map/map.actions'
import { getSettingsState } from '@containers/settings/settings.selectors'
import { Store, select } from '@ngrx/store'
import Feature from 'ol/Feature'
import Map from 'ol/Map'
import Overlay from 'ol/Overlay'
import View from 'ol/View'
import GeoJSON from 'ol/format/GeoJSON'
import { Draw, Modify, Snap } from 'ol/interaction'
import { Image as ImageLayer, Vector as VectorLayer } from 'ol/layer'
import { ImageWMS, TileWMS, Vector as VectorSource } from 'ol/source'
import { Circle as CircleStyle, Fill, Stroke, Style } from 'ol/style'
import { Subscription } from 'rxjs'
import { environment } from '@env/environment'
import Control from 'ol/control/Control'
import Bar from 'ol-ext/control/Bar'
import * as Extent from 'ol/extent'

@Component({
  selector: 'analytics-map',
  templateUrl: './analytics-map.component.html',
  styleUrls: ['./analytics-map.component.css'],
})
export class AnalyticsMapComponent implements OnInit, OnDestroy, AfterViewInit {
  public map: Map
  public draw: Draw
  public _polygonFeature: Draw
  public vector: VectorLayer<any>
  public streetsLayer: VectorLayer<any>
  public snap: Snap
  public overlay: Overlay
  public isPopupVisible: boolean
  public raster: ImageLayer<any>
  public pointFeature: Feature
  public modify: Modify
  public position: number[] = []
  public settings: any
  private settings$: Subscription
  public popupData: any = {}
  public extent: any
  public resolution: any
  public isCoordinatesPopupVisible = false
  public mainbar: Bar
  public activeLayers = []
  public options: any = {
    path: '/assets/loader.json',
  }
  public gradientColors = Array.from({ length: 20 }, (_, i) =>
    colorGradient(i / 19, 1),
  )
  @Input() public selectedLayer: any
  @Input() public data: any
  @Input() public isLoading: boolean

  @ViewChild('olPopup', { static: false }) popup: ElementRef

  constructor(public store: Store<any>) {}

  ngOnInit() {}

  ngAfterViewInit() {
    this.store.dispatch<any>(new SetDefaultData('dashboards'))

    this.settings$ = this.store
      .pipe(select(getSettingsState))
      .subscribe((state) => {
        this.settings = state.get('data')
        if (this.settings.size && !this.map) {
          this.initMap()
        }
      })
  }

  ngOnDestroy() {
    this.settings$.unsubscribe()

    this.clearMap()
  }

  clearMap() {
    this.map.getLayers().clear()

    // Remove all overlays (e.g., popups, markers)
    this.map.getOverlays().clear()

    // Remove all interactions (e.g., draw, select)
    this.map.getInteractions().clear()

    this.map = null
  }

  addControls() {
    this.addCoordinatesFinderButton()
  }

  setOpacity = (hex: string, alpha: number) =>
    hex
      ? `${hex}${Math.floor(alpha * 255)
          .toString(16)
          .padStart(2)}`
      : null

  initMap() {
    const {
      GIS_GEOSERVER_URL,
      GIS_GEOSERVER_WORKSPACE,
      GIS_BASE_LAYER,
      GIS_MAP_EXTENT,
    } = this.settings.toJS().gis
    this.overlay = new Overlay({
      element: this.popup.nativeElement,
      offset: [-128, 20],
      autoPan: false,
    })

    const extent = GIS_MAP_EXTENT.split(',').map((c: string) => parseFloat(c))
    const origin = extent[0] + ',' + extent[1]

    this.raster = new ImageLayer({
      source: new ImageWMS({
        url: `${GIS_GEOSERVER_URL}/${GIS_GEOSERVER_WORKSPACE}/wms`,
        params: {
          FORMAT: 'image/jpeg',
          VERSION: '1.1.1',
          tiled: true,
          LAYERS: `${GIS_GEOSERVER_WORKSPACE}:${GIS_BASE_LAYER}`,
          exceptions: 'application/vnd.ogc.se_inimage',
          tilesOrigin: origin,
        },
      }),
    })

    this.streetsLayer = new VectorLayer({
      zIndex: 100,
      source: new VectorSource({
        features: [],
      }),
    })

    this.map = new Map({
      layers: [this.raster, this.streetsLayer],
      target: 'ol-map',
      overlays: [this.overlay],
      view: new View({
        projection: 'EPSG:4326',
        center: this.position.length
          ? this.position
          : [(extent[0] + extent[2]) / 2, (extent[1] + extent[3]) / 2],
        zoom: 15,
      }),
    })

    this.map.getTargetElement().addEventListener('mouseleave', (e) => {
      this.isPopupVisible = false
    })

    this.map.on('pointermove', ({ pixel }) => {
      const feature = this.map.forEachFeatureAtPixel(pixel, function (feature) {
        return feature
      }) as any

      if (feature) {
        const geometry = feature.getGeometry() as any
        const coordinates = geometry.getCoordinates()
        let newCoordinates: any[]

        const { id, label } = feature.getPropertiesInternal()

        this.popupData = this.data[id] ? { label, ...this.data[id] } : {}

        this.isPopupVisible = true

        if (coordinates.length === 1) {
          var centroid = Extent.getCenter(geometry.getExtent())
          newCoordinates = centroid
        } else {
          newCoordinates = coordinates
        }

        this.overlay.setPosition(newCoordinates)
      } else {
        this.isPopupVisible = false
      }
    })

    this.addControls()
  }

  addCoordinatesFinderButton(): void {
    const button = document.createElement('button')
    const icon = document.createElement('img')
    icon.setAttribute('src', '/assets/search-icon.svg')
    button.appendChild(icon)
    button.addEventListener('click', () => {
      this.isCoordinatesPopupVisible = !this.isCoordinatesPopupVisible
    })
    button.className = 'coordinates-button'

    const controlDiv = document.createElement('div')
    controlDiv.className = 'coordinates-button-control'
    controlDiv.appendChild(button)

    const controlElement = document.createElement('div')
    controlElement.className = 'ol-unselectable ol-control coordinates-control'
    controlElement.appendChild(controlDiv)

    const mapControls = this.map.getControls()
    mapControls.push(
      new (class extends Control {
        constructor() {
          super({
            element: controlElement,
          })
        }
      })(),
    )
  }

  ngOnChanges(changes: SimpleChanges) {
    const _this = this
    if (
      (changes.selectedLayer && changes.selectedLayer.currentValue) ||
      (changes.data && changes.data.currentValue)
    ) {
      if (this.map && this.map.getAllLayers().length) {
        this.map.setLayers(
          this.map
            .getAllLayers()
            .filter((l) => !l.getPropertiesInternal().isDashboardLayer),
        )
      }
      if (changes.selectedLayer && changes.selectedLayer?.currentValue) {
        const source = new VectorSource({
          format: new GeoJSON(),
          url: `${environment.API_ENDPOINT}/gis/public/layer/${changes.selectedLayer.currentValue.id}`,
        })

        const vector = new VectorLayer({
          properties: {
            layerId: changes.selectedLayer.currentValue.id,
            name: changes.selectedLayer.currentValue.name,
            isDashboardLayer: true,
          },
          visible: true,
          source,
          style: function (feature: any) {
            const { id } = feature.getPropertiesInternal()
            const { percentage } = _this.data[id] || { percentage: 0 }
            const styles = _this.data[id]
              ? {
                  MultiPolygon: new Style({
                    stroke: new Stroke({
                      color: colorGradient(percentage / 100, 0.7),
                      width: 1,
                    }),
                    fill: new Fill({
                      color: colorGradient(percentage / 100, 0.6),
                    }),
                  }),
                }
              : {}
            return styles[feature.getGeometry().getType()]
          },
        })
        this.map.addLayer(vector)
      }
    }
  }
}
